<script setup lang="ts">
import { computed, ref, useTemplateRef } from 'vue';
import { uniqueId } from 'lodash-es';
import { INPUT_DEBOUNCE } from '@/modules/common/consts/ui.ts';
import { ErrorUtil } from '@/modules/common/utils/error.util.ts';
import { DOCUMENT_HINTS_ASYNC_THRESHOLD } from '@/modules/common/consts/document.ts';
import type { Option } from '@/modules/common/types/option.type.ts';
import { useAbort } from '@/modules/common/composables/use-abort.ts';
import { StringUtil } from '@/modules/common/utils/string.util.ts';
import type { FieldType, PartialQueryOptionsGetter, SelectEvent } from '@/modules/common/types/ui.ts';
import type { MaskInputOptions } from 'maska';
import UiDropdown from '@/modules/ui/components/ui/UiDropdown.vue';
import UiInput from '@/modules/ui/components/ui/UiInput.vue';
import UiTextarea from '@/modules/ui/components/ui/UiTextarea.vue';
import { IdPrefix } from '@/modules/common/enums/id-prefix.ts';
import { useDebounceFn } from '@vueuse/core';
import { notification } from '@/modules/common/composables/use-notification.ts';
import UiDropdownList from '@/modules/ui/components/ui/UiDropdownList.vue';
import { useI18n } from 'vue-i18n';

const model = defineModel<string>({ required: true, default: '' });

const {
  id = uniqueId(IdPrefix.Control),
  label,
  field = 'input',
  color = 'gray',
  size = 'm',
  placeholder = 'ui.prompt.enterValue',
  getHints,
} = defineProps<{
  id?: string;
  label?: string;
  error?: boolean;
  placeholder?: string;
  disabled?: boolean;
  getHints: PartialQueryOptionsGetter<string | number>;
  field?: FieldType;
  color?: 'gray' | 'white';
  size?: 'm' | 'l';
  maxLength?: number;
  rows?: number;
  noClear?: boolean;
  noResize?: boolean;
  autoGrow?: boolean;
  autoGrowMinHeight?: number;
  autoGrowMaxRows?: number;
  mask?: MaskInputOptions | string;
}>();

const emit = defineEmits<{
  focus: [];
  blur: [];
}>();

const { signal, abort } = useAbort();

const { maybeT } = useI18n();

const dropdownRef = useTemplateRef('dropdown');
const controlRef = useTemplateRef('control');

const hints = ref<Option[]>([]);
const isLoading = ref(false);
const isAsync = ref<boolean | null>(null);

const isInit = computed(() => isAsync.value !== null);

const filteredHints = computed(() => {
  if (isAsync.value || !model.value) {
    return hints.value;
  }

  return hints.value.filter((hint) => {
    const normalizedHint = StringUtil.normalize(hint.label);
    const normalizedCurrent = StringUtil.normalize(model.value);
    return normalizedHint.includes(normalizedCurrent) && hint.label.trim() !== model.value.trim();
  });
});

function onError(error: unknown) {
  if (!ErrorUtil.isUnexpected(error)) {
    return;
  }

  isLoading.value = false;

  notification.error(
    { key: 'ui.notification.error.field.getFrequentlyUsedValues', params: { field: maybeT(label || placeholder) } },
    error
  );
}

async function init() {
  if (isInit.value) {
    return;
  }

  try {
    isLoading.value = true;
    const initialHints = await getHints(signal.value);

    isAsync.value = initialHints.length >= DOCUMENT_HINTS_ASYNC_THRESHOLD;

    if (!isAsync.value) {
      hints.value = initialHints;
    }
  } catch (error) {
    onError(error);
  } finally {
    isLoading.value = false;
  }
}

function open() {
  if (!isLoading.value && filteredHints.value.length > 0) {
    dropdownRef.value?.show();
  }
}

function close() {
  dropdownRef.value?.hide();
}

function onFocus() {
  init();
  emit('focus');
}

const getHintsDebounced = useDebounceFn(async function (query: string, noOpen: boolean) {
  try {
    isLoading.value = true;
    hints.value = await getHints(signal.value, query);
    isLoading.value = false;

    if (!noOpen && controlRef.value?.isFocused) {
      open();
    }
  } catch (error) {
    onError(error);
  }
}, INPUT_DEBOUNCE);

function getAsyncHints(query: string, noOpen: boolean) {
  close();
  abort('A new value has been received');
  void getHintsDebounced(query, noOpen);
}

function getSyncHints(query: string) {
  if (query) {
    open();
  } else {
    close();
  }
}

function onInput(value: string, noOpen = false) {
  model.value = value;

  if (!isInit.value) {
    return;
  }

  if (isAsync.value) {
    getAsyncHints(value, noOpen);
  } else {
    getSyncHints(value);
  }
}

function onBlur() {
  emit('blur');
}

function onClear() {
  model.value = '';
}

function onKeydown(event: KeyboardEvent) {
  if (event.code === 'Tab') {
    close();
  }

  if ((event.metaKey || event.ctrlKey) && event.code === 'Space') {
    open();
  }
}

function onSelect({ option }: SelectEvent) {
  close();
  onInput(option.label, true);
}
</script>

<template>
  <UiDropdown
    ref="dropdown"
    class="ui-prompt"
    :offset="4"
    shrink
    anchor-width
    full-width
    close-if-inner-click
    close-if-outside-click
  >
    <template #anchor>
      <UiInput
        v-if="field === 'input'"
        :id
        ref="control"
        :placeholder="placeholder"
        :color="color"
        :size="size"
        :disabled="disabled"
        :error
        :max-length="maxLength"
        :mask
        :no-clear="noClear"
        autocomplete="off"
        :model-value="model"
        @update:model-value="onInput($event)"
        @focus="onFocus"
        @blur="onBlur"
        @clear="onClear"
        @keydown="onKeydown"
      >
        <template #postfix>
          <slot />
        </template>
      </UiInput>

      <UiTextarea
        v-else
        :id
        ref="control"
        :placeholder="placeholder"
        :disabled="disabled"
        :error
        :max-length="maxLength"
        :rows="rows"
        autocomplete="off"
        :no-resize="noResize"
        :auto-grow="autoGrow"
        :auto-grow-min-height="autoGrowMinHeight"
        :auto-grow-max-rows="autoGrowMaxRows"
        no-line-breaks
        :model-value="model"
        @update:model-value="onInput($event)"
        @focus="onFocus"
        @blur="onBlur"
        @keydown="onKeydown"
      >
        <slot />
      </UiTextarea>
    </template>

    <UiDropdownList
      :options="filteredHints"
      @select="onSelect"
    />
  </UiDropdown>
</template>
